/**
 * Portal data actions
 */

import { NgRedux } from '@angular-redux/store';
import { Injectable } from '@angular/core';
import { IAppState } from '@app/store';
import { IUserSession } from '@store/reducers/session';
import { generateId } from '@shared/utils';
import { rx } from '@shared/rx';
import { STATE_KEYS } from '@shared/services/state-service';
import {
  IAction,
  IEventPickerOptions,
  ISite,
  ISiteSelectorOptions,
  IIframeMessageType,
  IModalDialogResult,
  ENTITY_TYPES,
} from '@shared/types';
import { get, isEmpty, map } from 'lodash';
import { UiActions } from '@store/actions/ui/ui.actions';
import * as moment from 'moment';
import { TIME_FORMATS } from '@config/global-constants';
import { XVII18nService } from '@app/i18n/services/xvi-translate.service';

@Injectable()
export class XprtDataActions {
  public static FETCH_COMPANIES: string = 'FETCH_COMPANIES';
  public static FETCH_COMPANIES_SUCCESS: string = 'FETCH_COMPANIES_SUCCESS';
  public static FETCH_COMPANIES_ERROR: string = 'FETCH_COMPANIES_ERROR';

  public static SHOW_COMPANY_SELECTOR: string = 'SHOW_COMPANY_SELECTOR';
  public static HIDE_COMPANY_SELECTOR: string = 'HIDE_COMPANY_SELECTOR';

  public static SHOW_EVENT_SELECTOR: string = 'SHOW_EVENT_SELECTOR';
  public static EVENT_SELECTION_SUCCESS: string = 'EVENT_SELECTION_SUCCESS';
  public static EVENT_SELECTION_ERROR: string = 'EVENT_SELECTION_ERROR';
  public static EVENT_SELECTION_CANCEL: string = 'EVENT_SELECTION_CANCEL';

  public static FETCH_XPRT_INFO: string = 'FETCH_XPRT_INFO';
  public static FETCH_XPRT_INFO_SUCCESS: string = 'FETCH_XPRT_INFO_SUCCESS';
  public static FETCH_XPRT_INFO_ERROR: string = 'FETCH_XPRT_INFO_ERROR';

  public static USER_ACTIVITY_EVENT: string = 'USER_ACTIVITY_EVENT';

  public static GET_ACCESS_TOKEN: string = 'GET_ACCESS_TOKEN';
  public static GET_ACCESS_TOKEN_SUCCESS: string = 'GET_ACCESS_TOKEN_SUCCESS';
  public static GET_ACCESS_TOKEN_ERROR: string = 'GET_ACCESS_TOKEN_ERROR';

  public static FETCH_STORES: string = 'FETCH_STORES';
  public static FETCH_STORES_SUCCESS: string = 'FETCH_STORES_SUCCESS';
  public static FETCH_STORES_ERROR: string = 'FETCH_STORES_ERROR';

  public static SELECT_STORES: string = 'SELECT_STORES';
  public static SHOW_STORES_MODAL: string = 'SHOW_STORES_MODAL';
  public static HIDE_STORES_MODAL: string = 'HIDE_STORES_MODAL';

  public static SHOW_SITE_SELECTOR: string = 'SHOW_SITE_SELECTOR';

  public static getAccessToken(): IAction {
    return {
      type: XprtDataActions.GET_ACCESS_TOKEN,
    };
  }

  public static showCompanySelectorAction(): IAction {
    return {
      type: XprtDataActions.SHOW_COMPANY_SELECTOR,
    };
  }

  public static hideCompanySelectorAction(): IAction {
    return {
      type: XprtDataActions.HIDE_COMPANY_SELECTOR,
    };
  }

  public static showEventSelectorAction(
    msgOptions: IEventPickerOptions,
  ): IAction {
    return {
      type: XprtDataActions.SHOW_EVENT_SELECTOR,
      payload: msgOptions,
    };
  }

  public static eventSelectionSuccessAction(msg: IIframeMessageType): IAction {
    return {
      type: XprtDataActions.EVENT_SELECTION_SUCCESS,
      payload: msg,
    };
  }

  public static eventSelectionCancelAction(msg: IIframeMessageType): IAction {
    return {
      type: XprtDataActions.EVENT_SELECTION_CANCEL,
      payload: msg,
    };
  }

  public static eventSelectionErrorAction(msg: IIframeMessageType): IAction {
    return {
      type: XprtDataActions.EVENT_SELECTION_ERROR,
      payload: msg,
    };
  }

  /**
   * Companies
   */

  public static fetchCompaniesAction(companyIds?: string[]): IAction {
    return {
      type: XprtDataActions.FETCH_COMPANIES,
      payload: {
        companyIds,
      },
    };
  }

  public static fetchCompaniesSuccessAction(servicePayload): IAction {
    return {
      type: XprtDataActions.FETCH_COMPANIES_SUCCESS,
      payload: servicePayload,
    };
  }

  public static fetchCompaniesErrorAction(errors): IAction {
    return {
      type: XprtDataActions.FETCH_COMPANIES_ERROR,
      payload: {
        errors,
      },
    };
  }

  /*
   * Environment information
   */

  public static fetchInfoAction(): IAction {
    return {
      type: XprtDataActions.FETCH_XPRT_INFO,
    };
  }

  public static fetchInfoSuccessAction(info): IAction {
    return {
      type: XprtDataActions.FETCH_XPRT_INFO_SUCCESS,
      payload: {
        info,
      },
    };
  }

  public static fetchInfoErrorAction(errors): IAction {
    return {
      type: XprtDataActions.FETCH_XPRT_INFO_ERROR,
      payload: {
        errors,
      },
    };
  }

  /**
   * STORES
   */
  public static fetchStoresAction(query?: any): IAction {
    return {
      type: XprtDataActions.FETCH_STORES,
      payload: {
        query,
      },
    };
  }

  public static fetchStoresSuccessAction(servicePayload): IAction {
    return {
      type: XprtDataActions.FETCH_STORES_SUCCESS,
      payload: servicePayload,
    };
  }

  public static fetchStoresErrorAction(errors): IAction {
    return {
      type: XprtDataActions.FETCH_STORES_ERROR,
      payload: {
        errors,
      },
    };
  }

  public static selectStoresAction(selectedStores: any[]): IAction {
    return {
      type: XprtDataActions.SELECT_STORES,
      payload: {
        selectedStores,
      },
    };
  }

  public static showStoresModalAction(options): IAction {
    return {
      type: XprtDataActions.SHOW_STORES_MODAL,
      payload: { options },
    };
  }

  public static hideStoresModalAction(): IAction {
    return {
      type: XprtDataActions.HIDE_STORES_MODAL,
    };
  }

  public static showSiteSelectorAction(msgOptions: object): IAction {
    return {
      type: XprtDataActions.SHOW_SITE_SELECTOR,
      payload: msgOptions,
    };
  }

  public constructor(
    private ngRedux: NgRedux<IAppState>,
    private uiActions: UiActions,
    private i18nService: XVII18nService,
  ) {}

  public async postMessageResultAsPromised(messageId: string): Promise<any> {
    const result = await this.ngRedux
      .select(STATE_KEYS.IFRAME_POST_MESSAGE_RESULT)
      .pipe(rx.find((res: any) => get(res, 'options.messageId') === messageId))
      .toPromise();

    return !isEmpty(result.errors) ? null : result.response.payload;
  }

  public fetchCompanies(companyIds?: string[]): void {
    this.ngRedux.dispatch(XprtDataActions.fetchCompaniesAction(companyIds));
  }

  public showCompanySelector(): void {
    this.ngRedux.dispatch(XprtDataActions.showCompanySelectorAction());
  }
  public hideCompanySelector(): void {
    this.ngRedux.dispatch(XprtDataActions.hideCompanySelectorAction());
  }

  public fetchInfo(): void {
    this.ngRedux.dispatch(XprtDataActions.fetchInfoAction());
  }

  public getAccessToken(): void {
    this.ngRedux.dispatch(XprtDataActions.getAccessToken());
  }

  public async showEventSelector(
    selectorOptions: any = { required: false, multiselect: false },
  ): Promise<IModalDialogResult> {
    const msgOptions = {
      messageId: generateId(),
      locale: moment.locale(),
      dateFormats: { event_days: `${TIME_FORMATS.DDD} ${TIME_FORMATS.L}` },
    };
    this.ngRedux.dispatch(
      XprtDataActions.showEventSelectorAction({
        ...selectorOptions,
        ...msgOptions,
      }),
    );

    const result = await this.postMessageResultAsPromised(msgOptions.messageId);
    if (!result) {
      return result;
    }

    try {
      const translated = this.i18nService.applyUserDefinedTranslations(
        ENTITY_TYPES.EVENTS,
        result,
      );

      return {
        ...result,
        items: translated?.items ?? result.items,
      };
    } catch (error) {
      return result;
    }
  }

  public eventSelectionSuccess(msg: IIframeMessageType): void {
    this.ngRedux.dispatch(XprtDataActions.eventSelectionSuccessAction(msg));
  }

  public eventSelectionCancel(msg: IIframeMessageType): void {
    this.ngRedux.dispatch(XprtDataActions.eventSelectionCancelAction(msg));
  }

  public eventSelectionError(msg: IIframeMessageType): void {
    this.ngRedux.dispatch(XprtDataActions.eventSelectionErrorAction(msg));
  }

  /* helpers */
  public getCurrentCompanyId(): string {
    const session: IUserSession = this.ngRedux.getState().userSession;

    return session.currentCompanyId;
  }

  public fetchStores(query?: any): void {
    this.ngRedux.dispatch(XprtDataActions.fetchStoresAction(query));
  }

  public selectStores(selectedStores: any[]): void {
    this.ngRedux.dispatch(XprtDataActions.selectStoresAction(selectedStores));
  }

  public showStoresModal(
    options = { forceSelect: false, masterList: true },
  ): void {
    this.ngRedux.dispatch(XprtDataActions.showStoresModalAction(options));
  }

  public hideStoresModal(): void {
    this.ngRedux.dispatch(XprtDataActions.hideStoresModalAction());
  }

  public async showSiteSelector(
    siteSelectorOptions: ISiteSelectorOptions = {
      required: false,
      multiselect: false,
    },
    options: any = {},
    suppressHistoryBack: boolean = false,
  ): Promise<ISite[]> {
    const msgOptions = { messageId: generateId() };
    const isEnterpriseCompany = get(options, 'isEnterpriseCompany', false);
    const pageType = get(options, 'pageType');

    if (isEnterpriseCompany) {
      siteSelectorOptions.required = true;
    }

    this.ngRedux.dispatch(
      XprtDataActions.showSiteSelectorAction({
        ...siteSelectorOptions,
        ...msgOptions,
      }),
    );

    const result = await this.postMessageResultAsPromised(msgOptions.messageId);

    if (siteSelectorOptions.applySiteSelection) {
      if (!result) {
        if (!suppressHistoryBack) {
          this.uiActions.historyBack({ isEnterpriseCompany, pageType });
        }
      } else {
        const storesIds = map(result.items, 'id');

        this.selectStores(storesIds);
      }
    }

    return result ? result.items : null;
  }
}
